import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { useIsMobile } from '../lib/useIsMobile.js';
import DashboardPicker from './DashboardPicker.jsx';
import Icon from './Icon.jsx';
import ShareSendPanel from './ShareSendPanel.jsx';
import SharePreview from './SharePreview.jsx';

const EXPIRY_CHOICES = [[7, '7 days'], [30, '30 days'], [90, '90 days'], [365, '1 year']];

// The create/edit sheet for a shareable link, on BOTH the surfaces that mint one:
//   • Settings / Admin → Share links — pick any of the client's reports (the
//     browsable event → category → report tree).
//   • A dashboard's ⋯ menu — the report is already decided, so `fixedTarget`
//     hides the picker and the sender's CURRENT filters are frozen into the link.
// One component, so the two can't drift into different rules.
// ── Create / edit ─────────────────────────────────────────────────────────────
// Tiles are ticked in, never out: the sheet loads the dashboard's tiles and the
// link stores the ones chosen. A tile added to the dashboard later isn't in that
// list, so it stays off the link until someone deliberately adds it.
export default function ShareLinkEditor({ scope, entityId, link, reports = [], fixedTarget = null, filterValues = null, onClose, onCreated }) {
  const isMobile = useIsMobile();
  const apiScope = scope === 'my' ? 'my' : 'admin-client';
  // The picker's value is an (event, report) PAIR — the event is what pins the
  // link to one festival, since its locked filters ride into every tile query.
  const [pick, setPick] = useState(
    fixedTarget ? `${fixedTarget.suiteId || ''}|${fixedTarget.dashboardId}`
      : link ? `${link.suiteId || ''}|${link.dashboardId}` : '',
  );
  const [suiteId, dashboardId] = pick ? [pick.slice(0, pick.indexOf('|')), pick.slice(pick.indexOf('|') + 1)] : ['', ''];
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({
    title: link?.title || fixedTarget?.title || '',
    note: link?.note || '',
    mode: link?.mode || 'open',
    // On EDIT, null = "leave the expiry exactly as it is" — the server only
    // rewrites expiry when this is a number. Sending 30 on every edit silently
    // dropped a 1-year link to 30 days (and, before the server fix, un-revoked
    // it) whenever the sender touched anything else (2026-08-04 review #1).
    expiresInDays: link ? null : 30,
    allowedEmails: (link?.allowedEmails || []).join('\n'),
    // The readable part of the address; the server adds a short random code.
    slug: (link?.slug || '').replace(/-[a-f0-9]{6}$/, ''),
  });
  const [chosen, setChosen] = useState(() => new Set(link?.visibleTiles || []));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  // Preview & arrange, reachable from HERE as well as the register — a link made
  // from a dashboard's ⋯ menu never passes through the register, so without this
  // the sender had no way to check (or rearrange) what they'd just made.
  const [previewing, setPreviewing] = useState(null);

  useEffect(() => {
    if (!dashboardId) { setTarget(null); return; }
    api.shareReportTarget(apiScope, entityId, dashboardId, suiteId)
      .then((t) => {
        setTarget(t);
        setForm((f) => ({ ...f, title: f.title || t.title }));
        // New link → everything on by default, so the common case is one tap and
        // hiding is the deliberate act.
        if (!link) setChosen(new Set(t.tiles.map((x) => x.id)));
      })
      .catch((e) => { setTarget(null); setError(e.message); });
  }, [apiScope, entityId, dashboardId, suiteId, link]);

  const toggle = (id) => setChosen((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  async function save() {
    setBusy(true); setError('');
    try {
      const body = {
        ...form, dashboardId, suiteId, visibleTiles: [...chosen], allowedEmails: form.allowedEmails,
        // Created from a dashboard: freeze what the sender is LOOKING AT, so the
        // reader opens the same view (their own filters are not offered).
        ...(filterValues ? { filterValues } : {}),
      };
      // Only send expiry when the sender actually chose one — null (an untouched
      // edit) must leave the link's existing TTL and revoked state alone.
      if (body.expiresInDays == null) delete body.expiresInDays;
      if (link) { await api.shareReportUpdate(apiScope, link.id, body); onClose(true); }
      else {
        const made = await api.shareReportCreate(apiScope, entityId, body);
        setCreated(made);
        onCreated?.(made);
      }
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  if (previewing) {
    return <SharePreview scope={scope} link={previewing} onClose={() => setPreviewing(null)} backLabel="← Back" />;
  }
  if (created) {
    return <CreatedPanel link={created} scope={scope} onPreview={() => setPreviewing(created)} onDone={() => onClose(true)} />;
  }

  const tiles = target?.tiles || [];
  return (
    <div>
      {!fixedTarget && <button style={back} onClick={() => onClose(false)}>← Back to links</button>}
      <h3 style={{ fontSize: 16, fontWeight: 700, margin: fixedTarget ? '0 0 4px' : '12px 0 4px' }}>{link ? 'Edit link' : 'New shareable link'}</h3>
      <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
        {fixedTarget
          ? `${fixedTarget.title || 'This report'} — shared live, with the filters you're looking at now. The reader sees fresh numbers every time they open it.`
          : 'The reader sees live numbers, refreshed every time they open it — never a stale export.'}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 620 }}>
        {!link && !fixedTarget && (
          <Row label="Report">
            {/* The SAME browsable event → category → report picker Goals, Alerts
                and Live updates use. The branch matters here as well as the leaf:
                the event is what pins the link to one festival. */}
            <DashboardPicker
              value={dashboardId}
              onChange={(id, row) => setPick(`${row?.suiteId || ''}|${id}`)}
              dashboards={reports}
              isMobile={isMobile}
              placeholder="Choose an event and report…"
            />
            {reports.length === 0 && (
              <p style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>
                This client has no reports yet — add a dashboard to one of their events first.
              </p>
            )}
            {suiteId && (
              <p style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>
                Pinned to this event — the reader only ever sees this event's numbers.
              </p>
            )}
          </Row>
        )}
        <Row label="Title the reader sees">
          <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} style={input} placeholder="e.g. Bushfire 2026 — sponsor report" />
        </Row>
        <Row label="Link address (optional)">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{`${window.location.origin}/r/`}</span>
            <input
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              style={{ ...input, flex: 1, minWidth: 160 }}
              placeholder="kff-sponsor-pack"
            />
          </div>
          <p style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
            Makes the link readable when you send it. We add a short code on the end
            (…-9f3k2q) so the address still can't be guessed. Leave blank for the
            standard link. {link ? 'Changing it keeps the old address working.' : ''}
          </p>
        </Row>

        <Row label="A short note (optional)">
          <textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} style={{ ...input, minHeight: 70, resize: 'vertical' }} placeholder="Anything you want them to read first." />
        </Row>

        <Row label="Who can open it">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Choice
              checked={form.mode === 'open'} onChange={() => setForm((f) => ({ ...f, mode: 'open' }))}
              title={<><Icon name="email" size={14} /> Anyone with the link — no Pulse account</>}
              body="They give their name, email and mobile, then confirm by clicking a link we email them. You see exactly who opened it."
            />
            <Choice
              checked={form.mode === 'closed'} onChange={() => setForm((f) => ({ ...f, mode: 'closed' }))}
              title={<><Icon name="lock" size={14} /> Pulse login required</>}
              body="Only people who already have a Pulse login with access to this report can open it."
            />
          </div>
        </Row>

        {form.mode === 'open' && (
          <Row label="Restrict to specific people (optional)">
            <textarea
              value={form.allowedEmails}
              onChange={(e) => setForm((f) => ({ ...f, allowedEmails: e.target.value }))}
              style={{ ...input, minHeight: 74, resize: 'vertical', fontFamily: 'inherit' }}
              placeholder={'sam@sponsor.co.za\n@agency.co.za'}
            />
            <p style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
              Leave blank and anyone with the link can open it, after confirming their own email — you'll still see exactly who did.
              List addresses (one per line) and only those people can even ask; a forwarded link is useless to anyone else.
              Add a whole company with <code>@theirdomain.co.za</code>.
            </p>
          </Row>
        )}

        <Row label="Expires after">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {EXPIRY_CHOICES.map(([days, label]) => (
              <button key={days} onClick={() => setForm((f) => ({ ...f, expiresInDays: days }))} style={{ ...mini, ...(form.expiresInDays === days ? { borderColor: 'var(--brand)', color: 'var(--brand)', fontWeight: 700 } : null) }}>{label}</button>
            ))}
          </div>
          {link && (
            <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
              {form.expiresInDays == null
                ? `Currently expires ${link.expiresAt ? new Date(link.expiresAt).toLocaleDateString() : 'never'} — pick an option above only if you want to change it.`
                : 'The expiry (and the on/off state) will be reset to your choice above.'}
            </p>
          )}
        </Row>

        <Row label={`What they see (${chosen.size}/${tiles.length} tiles)`}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <button style={mini} onClick={() => setChosen(new Set(tiles.map((t) => t.id)))}>Select all</button>
            <button style={mini} onClick={() => setChosen(new Set())}>Clear all</button>
          </div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, maxHeight: isMobile ? 260 : 320, overflowY: 'auto' }}>
            {tiles.length === 0 ? (
              <div style={{ padding: 14, fontSize: 13, color: 'var(--muted)' }}>Pick a report to choose its tiles.</div>
            ) : tiles.map((t) => (
              <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', borderBottom: '1px solid var(--hairline)', cursor: 'pointer', minHeight: 44 }}>
                <input type="checkbox" checked={chosen.has(t.id)} onChange={() => toggle(t.id)} style={{ width: 18, height: 18, flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 13.5 }}>{t.title || <em style={{ color: 'var(--muted)' }}>Untitled tile</em>}</span>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>{t.type === 'text' ? 'text' : (t.vis || 'chart')}</span>
              </label>
            ))}
          </div>
          <p style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>
            Unticked tiles aren't sent to the reader's browser at all — they're removed on the server, not just hidden.
          </p>
        </Row>

        {error && <div style={{ fontSize: 13, color: '#c0392b' }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...primary, opacity: busy || !chosen.size || !dashboardId ? 0.55 : 1 }} disabled={busy || !chosen.size || !dashboardId} onClick={save}>
            {busy ? 'Saving…' : link ? 'Save changes' : 'Create link'}
          </button>
          <button style={secondary} onClick={() => onClose(false)}>Cancel</button>
          {link && <button style={secondary} onClick={() => setPreviewing(link)}>Preview &amp; arrange</button>}
        </div>
      </div>
    </div>
  );
}

function CreatedPanel({ link, scope, onPreview, onDone }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link.url); } catch { window.prompt('Copy this link:', link.url); }
    setCopied(true); setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div style={{ ...card, maxWidth: 620 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700 }}>Link ready</h3>
      <p style={{ fontSize: 13, color: 'var(--muted)', margin: '6px 0 12px' }}>
        Send this to whoever needs it. {link.mode === 'closed' ? 'They\'ll need their Pulse login.' : 'They\'ll enter their details and confirm by email before anything loads.'}
      </p>
      <div style={{ padding: '11px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg)', fontSize: 12.5, wordBreak: 'break-all' }}>{link.url}</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button style={primary} onClick={copy}>{copied ? '✓ Copied' : 'Copy link'}</button>
        <button style={secondary} onClick={onPreview}>Preview &amp; arrange</button>
        <button style={secondary} onClick={onDone}>Done</button>
      </div>
      {/* Send it now, while you're here — copying the URL into your own mail client
          works, but it loses the branding and adds a step at the worst moment. */}
      <div style={{ marginTop: 14 }}>
        <ShareSendPanel scope={scope} link={link} />
      </div>
    </div>
  );
}


const card = { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 13 };
const primary = { padding: '10px 16px', minHeight: 42, borderRadius: 10, border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' };
const secondary = { padding: '10px 16px', minHeight: 42, borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const mini = { padding: '7px 11px', minHeight: 36, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' };
const back = { background: 'none', border: 'none', color: 'var(--brand)', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 };
const input = { padding: '11px 12px', minHeight: 42, fontSize: 15, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', width: '100%' };

function Row({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>{label}</span>
      {children}
    </label>
  );
}

function Choice({ checked, onChange, title, body }) {
  return (
    <button
      onClick={onChange}
      style={{
        textAlign: 'left', padding: '12px 13px', borderRadius: 10, cursor: 'pointer', background: 'var(--card)',
        border: `1px solid ${checked ? 'var(--brand)' : 'var(--border)'}`, boxShadow: checked ? '0 0 0 1px var(--brand)' : 'none',
      }}
    >
      <div style={{ fontSize: 13.5, fontWeight: 700 }}>{title}</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3, lineHeight: 1.5 }}>{body}</div>
    </button>
  );
}
